import React, { useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { type LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'

import { Modal } from '../../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'

import { useAppSelector } from '../../../../store/hooks'
import { selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../common/integrations'
import { toLocalISODate } from '../../../../common/dateUtils'

import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'
import { DueDateInput } from './DueDateInput'
import { CrmRichTextEditor } from './CrmRichTextEditor'
import { useCrmAssignee } from './useCrmAssignee'

const PRIORITY_OPTIONS: SelectOption[] = [
  { id: 'Low', label: 'Low' },
  { id: 'Medium', label: 'Medium' },
  { id: 'High', label: 'High' },
]
const DEFAULT_PRIORITY_OPTION = PRIORITY_OPTIONS[1]

export const AdvisorEngineTaskActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useCrmEligibility(IntegrationServiceId.ADVISOR_ENGINE, results ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.ADVISOR_ENGINE,
    sourceObject: 'task',
    entityType: 'task',
    relatedToType: 'contact',
    successToast: { title: 'Creating task', message: 'AdvisorEngine task creation started.' },
    errorToast: { title: 'Failed to create task', message: 'Could not start the AdvisorEngine task creation. Please try again.' },
  })

  const [subject, setSubject] = useState('')
  const descriptionEditorRef = useRef<LexicalEditor>(null)
  const [descriptionText, setDescriptionText] = useState('')
  const [dueDate, setDueDate] = useState<Date | null>(null)
  const [selectedPriority, setSelectedPriority] = useState<SelectOption>(DEFAULT_PRIORITY_OPTION)

  // `zocksAssignedTo` is the current user (the task creator); the picked teammate only
  // supplies the external `assignedTo` for the CRM.
  const currentUser = useAppSelector(selectAuthenticatedUser)
  const clientOwner: string | undefined = eligibility.status === 'ready' && eligibility.eligibleCount === 1 ? eligibility.eligibleTargets[0].ownerId : undefined
  const { selectedAssignee, selectedAssigneeExternalId, assigneeOptions, selectedAssigneeOption, handleAssigneeOptionSelected } = useCrmAssignee(
    IntegrationServiceId.ADVISOR_ENGINE,
    clientOwner,
  )

  const count = results?.length ?? 0
  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'} in AdvisorEngine`
  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  // Subject, assignee, and due date are required; description is optional. `selectedPriority` always holds a value.
  const isFormValid = subject.trim().length > 0 && dueDate !== null && selectedAssigneeExternalId !== null

  const handleSave = async () => {
    if (!isFormValid) {
      return
    }

    const zocksTask: Record<string, unknown> = {
      task: subject.trim(),
      priority: selectedPriority.id,
      status: 'open',
    }
    const editor = descriptionEditorRef.current
    if (editor && descriptionText.trim().length > 0) {
      zocksTask.description = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
    }
    if (dueDate) {
      zocksTask.dueDate = toLocalISODate(dueDate)
    }
    if (selectedAssignee && selectedAssigneeExternalId) {
      // The picked teammate only supplies the external CRM assignee.
      zocksTask.assignedTo = selectedAssigneeExternalId
    }
    // `zocksAssignedTo` is the current user, required by integrations-service SaveTaskDetailsDTO.
    if (currentUser?.id) {
      zocksTask.zocksAssignedTo = {
        type: 'internal',
        id: currentUser.id,
        user: {
          name: currentUser.name,
          type: 'internal',
        },
      }
    }

    await save({ tasks: [zocksTask] })
  }

  const body =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='AdvisorEngine' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='AdvisorEngine' />
    ) : (
      <div className='d-flex flex-column gap-3'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <Form.Group controlId='advisorEngineTaskSubject' className='d-flex flex-column'>
          <Form.Label className='bold'>
            Task name <span className='text-danger'>*</span>
          </Form.Label>
          <Form.Control value={subject} onChange={(e) => setSubject(e.target.value)} placeholder='Enter task name' disabled={saving} />
        </Form.Group>
        <Form.Group controlId='advisorEngineTaskDescription' className='d-flex flex-column'>
          <Form.Label className='bold'>Task description</Form.Label>
          <CrmRichTextEditor ref={descriptionEditorRef} variant='task' onTextContentChange={setDescriptionText} placeholder={<>Enter task description…</>} />
        </Form.Group>
        <Row className='g-3'>
          <Form.Group as={Col} controlId='advisorEngineTaskAssignee' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Assignee <span className='text-danger'>*</span>
            </Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={assigneeOptions}
              initialSelectedOption={selectedAssigneeOption}
              onOptionSelected={handleAssigneeOptionSelected}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='advisorEngineTaskDueDate' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Due date <span className='text-danger'>*</span>
            </Form.Label>
            <DatePicker
              selected={dueDate}
              onChange={(date: Date | null) => setDueDate(date)}
              todayButton='Today'
              dateFormat='dd/MM/yyyy'
              disabled={saving}
              customInput={<DueDateInput />}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='advisorEngineTaskPriority' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>Priority</Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={PRIORITY_OPTIONS}
              initialSelectedOption={selectedPriority}
              onOptionSelected={setSelectedPriority}
            />
          </Form.Group>
        </Row>
        <CrmEligibilityNotice eligibility={eligibility} verbPhrase='create a task for' crmName='AdvisorEngine' />
      </div>
    )

  const primaryActionDisabled = noEligibleClients ? false : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || !isFormValid

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>Create task to {targetLabel}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Create task'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
